import { PILL_COLORS } from "components/ui/Pill/pillColors";

const CDM_STATUS_CONFIG = {
  ACCEPTED: {
    label: "Accepted",
    colors: PILL_COLORS.statusSuccess,
  },
  REIMBURSEMENT_REJECTED: {
    label: "Reimbursement Rejected",
    colors: PILL_COLORS.statusError,
  },
  CLINICAL_REJECTED: {
    label: "Clinical Rejected",
    colors: PILL_COLORS.statusError,
  },
  ILLEGAL_STATE: {
    label: "Illegal State",
    colors: PILL_COLORS.statusError,
  },
  PENDING: {
    label: "Pending",
    colors: PILL_COLORS.statusPending,
  },
  // Async PCRS acknowledgement: a successful sync send now only gets the review
  // as far as SENT, and the ACK that arrives later finalises it (AA ->
  // ACKNOWLEDGED, AE/AR -> REJECTED). So SENT is an in-flight state, not an
  // outcome, and the CDM status response may also report AWAITING_ACK.
  //
  // Both read as "in progress" (blue) rather than a warning: waiting for the ACK
  // is the normal path, and the review needs no attention until it resolves.
  // MNCMS badges its own AWAITING_ACK amber, but there it sits alongside NO_ACK
  // and means delivery was never confirmed.
  SENT: {
    label: "Awaiting acknowledgement",
    colors: PILL_COLORS.statusPending,
  },
  AWAITING_ACK: {
    label: "Awaiting acknowledgement",
    colors: PILL_COLORS.statusPending,
  },
  APPROVED: {
    label: "Approved",
    colors: PILL_COLORS.statusSuccess,
  },
  REJECTED: {
    label: "Rejected",
    colors: PILL_COLORS.statusError,
  },
  // A cancelled review is not a rejection: nothing was refused, the practice
  // withdrew it. Neutral rather than red, so it is not read as a PCRS problem
  // needing action.
  CANCELLED: {
    label: "Cancelled",
    colors: PILL_COLORS.statusNoStatus,
  },
  FAILED: {
    label: "Failed",
    colors: PILL_COLORS.statusError,
  },
  SUCCESS: {
    label: "Success",
    colors: PILL_COLORS.statusSuccess,
  },
  ACKNOWLEDGED: {
    label: "Acknowledged",
    colors: PILL_COLORS.statusAcknowledged,
  },
};

const getStatusConfig = (status) => {
  if (!status) return null;
  return CDM_STATUS_CONFIG[status.toUpperCase()] ?? null;
};

// The backend's unified status (CdmUnifiedStatus) has no CANCELLED. Cancelling a
// review sets both message statuses to CANCELLED, which is not ACKNOWLEDGED, so
// its derivation falls through to REIMBURSEMENT_REJECTED — telling the GP that
// PCRS refused a claim that was never made. The raw message statuses do carry
// the cancellation, so they are trusted over the derived value.
//
// Front-end only, deliberately: this belongs in CdmUnifiedStatus.fromSubmissionStatus,
// but a client-side resolve fixes what users see today without waiting on a
// backend release. It stays correct once the backend does return CANCELLED, and
// can then be reduced to `status ?? ...`.
export const resolveCdmReviewStatus = ({
  status,
  clinicalMessageStatus,
  reimbursementMessageStatus,
} = {}) => {
  const isCancelled =
    clinicalMessageStatus === "CANCELLED" ||
    reimbursementMessageStatus === "CANCELLED";

  if (isCancelled) return "CANCELLED";

  return status ?? clinicalMessageStatus ?? null;
};

export const getCdmStatusColors = (status) => {
  return getStatusConfig(status)?.colors ?? PILL_COLORS.statusNoStatus;
};

export const formatCdmStatus = (status) => {
  if (!status) return "No status";
  return getStatusConfig(status)?.label ?? status;
};
